import { forwardRef } from 'react';
import { Link } from 'react-router-dom';
import { Pause, Play, RotateCcw, RotateCw, X } from 'lucide-react';
import { Button, Card, cn } from '@ai-tutor/ui';
import { SeekBar } from './PodcastControls';

export interface PodcastPanelProps {
  /** The waveform the seek bars are drawn from. */
  peaks: number[];
  lessonTitle: string;
  sectionIndex: number;
  sectionTitle: string;
  isPlaying: boolean;
  currentTime: number;
  duration: number;
  /** The lesson's Podcast tab, opened on this section's transcript. */
  transcriptHref: string;
  onToggle: () => void;
  onSeek: (time: number) => void;
  onClose: () => void;
  onNavigate: () => void;
  className?: string;
}

/** What the podcast pill opens: where it is, seeking, and the way to its
 *  transcript - without leaving the page you are on. */
export const PodcastPanel = forwardRef<HTMLDivElement, PodcastPanelProps>(
  (props, ref) => {
    const { currentTime, duration, onSeek } = props;
    return (
      <Card
        ref={ref}
        elevation='md'
        role='dialog'
        aria-label='Podcast player'
        className={cn(
          'w-80 space-y-4 bg-popover p-4 dark:bg-popover',
          props.className
        )}
      >
        <div className='flex items-start justify-between gap-3'>
          <div className='min-w-0'>
            <p className='font-mono text-xs text-muted-foreground'>
              Podcast · Section {props.sectionIndex}
            </p>
            <p className='mt-1 truncate text-sm font-semibold text-foreground'>
              {props.sectionTitle}
            </p>
            <p className='truncate text-xs text-muted-foreground'>
              {props.lessonTitle}
            </p>
          </div>
          <button
            type='button'
            onClick={props.onClose}
            aria-label='Stop and close the podcast'
            className='tap-target -mr-1 rounded-md p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground'
          >
            <X className='h-4 w-4' aria-hidden='true' />
          </button>
        </div>
        <SeekBar
          peaks={props.peaks}
          currentTime={currentTime}
          duration={duration}
          onSeek={onSeek}
        />
        <PanelTransport {...props} />
        <Link
          to={props.transcriptHref}
          onClick={props.onNavigate}
          className='block text-sm font-medium text-primary underline-offset-4 hover:underline'
        >
          Open transcript
        </Link>
      </Card>
    );
  }
);
PodcastPanel.displayName = 'PodcastPanel';

function PanelTransport(props: PodcastPanelProps) {
  const { currentTime, duration, onSeek } = props;
  return (
    <div className='flex items-center gap-2'>
      <Button
        variant='outline'
        size='sm'
        aria-label='Back 10 seconds'
        onClick={() => onSeek(currentTime - 10)}
        className='max-sm:h-11'
      >
        <RotateCcw className='h-4 w-4' aria-hidden='true' />
      </Button>
      <Button size='sm' onClick={props.onToggle} className='flex-1 max-sm:h-11'>
        {props.isPlaying ? (
          <Pause className='mr-1.5 h-4 w-4' aria-hidden='true' />
        ) : (
          <Play className='mr-1.5 h-4 w-4' aria-hidden='true' />
        )}
        {props.isPlaying ? 'Pause' : 'Play'}
      </Button>
      <Button
        variant='outline'
        size='sm'
        aria-label='Forward 10 seconds'
        onClick={() => onSeek(Math.min(duration, currentTime + 10))}
        className='max-sm:h-11'
      >
        <RotateCw className='h-4 w-4' aria-hidden='true' />
      </Button>
    </div>
  );
}
